// @flow strict
import { useEffect, useMemo, useRef, useState } from 'react';
import type { TypeaheadScope, TypeaheadItem } from '../types';
import useSearchSessionId from './useSearchSessionId';
import parseSearchResults from './parseSearchResults';
import useSearchResultsWithImages from './useSearchResultsWithImages';
import useSearchTracking from './useSearchTracking';

const useThrottledState = <T>(currentValue: T, maxTimeout: number): T => {
  const [throttledValue, setThrottledValue] = useState<T>(currentValue);
  const lastTime = useRef(Date.now());

  useEffect(() => {
    const now = Date.now();
    const timeout = maxTimeout - (now - lastTime.current);

    if (timeout <= 0) {
      lastTime.current = now;
      setThrottledValue(currentValue);
      return undefined;
    }

    const handle = setTimeout(() => {
      lastTime.current = Date.now();
      setThrottledValue(currentValue);
    }, timeout);
    return () => clearTimeout(handle);
  }, [currentValue, maxTimeout]);

  return throttledValue;
};

/**
 * Constant parameters for /TypeAheadJson
 */
const typeAheadParams = {
  details: 'true',
  disableMaxGroupSize: 'true',
  geoBoostFix: 'true',
  geoPages: 'true',
  injectLists: 'false',
  injectNewLocation: 'true',
  interleaved: 'true',
  link_type: 'geo',
  local: 'true',
  matchKeywords: 'true',
  matchOverview: 'true',
  matchUserProfiles: 'true',
  matchTags: 'true',
  matchGlobalTags: 'true',
  nearPages: 'true',
  nearPagesLevel: 'strict',
  neighborhood_geos: 'true',
  scoreThreshold: '0.8',
  strictAnd: 'false',
  supportedSearchTypes: 'find_near_stand_alone_query',
  typeahead1_5: 'true',
  simple_typeahead: 'true',
  matchQuerySuggestions: 'true',
  rescue: 'true',
  scopeFilter: 'global',
  types: [
    'geo',
    'hotel',
    'eat',
    'attr',
    'vr',
    'air',
    'theme_park',
    'al',
    'act',
    'train',
    'uni',
    'shop',
    'port',
    'general_hospital',
    'ferry',
    'corp',
    'ship',
    'cruise_line',
    'car',
  ].join(','),
};

/* Pass in all queries, let the server side decide which ones to process */
export const MIN_QUERY_LENGTH = 1;

/**
 * A React hook that handles communication with the typeahead API. This is designed for the single
 * search MVP.
 *
 * @todo replace this with a GraphQL query
 */
const useSearchResults = (
  query: string,
  source: string,
  scope: TypeaheadScope,
  maxItems: number = 10,
) => {
  const searchSessionId = useSearchSessionId();

  const [results, setResults] = useState<TypeaheadItem[]>([]);

  /*
   * True if we have a response from the typeahead endpoint. If this is true, we don't show the
   * placeholder, even if the list of results is empty.
   */
  const [hasResponse, setHasResponse] = useState(false);

  const scopeParams = useMemo(() => {
    if (scope.latitude != null && scope.longitude != null) {
      return {
        isNearby: 'true',
        position: `${String(scope.latitude)},${String(scope.longitude)}`,
      };
    }

    if (scope.geoId != null && scope.geoId > 1) {
      return {
        parentids: String(scope.geoId),
        scope: String(scope.geoId),
        beforeGeoId: String(scope.geoId),
        afterGeoId: String(scope.geoId),
      };
    }

    return {
      parentids: '',
    };
  }, [scope.geoId, scope.latitude, scope.longitude]);

  const { logImpression, logSelection, logSearchAction, startTime } = useSearchTracking(
    searchSessionId,
    source,
    query,
    scopeParams,
  );

  const throttledQuery = useThrottledState(query, 100);
  const url = useMemo(() => {
    if (throttledQuery.length < MIN_QUERY_LENGTH || maxItems === 0) {
      return null;
    }

    return `/TypeAheadJson?${new URLSearchParams({
      action: 'API',
      searchSessionId,
      source,
      uiOrigin: source,
      max: String(maxItems),
      startTime,
      query: throttledQuery,
      beforeQuery: '',
      afterQuery: throttledQuery,
      ...scopeParams,
      ...typeAheadParams,
    }).toString()}`;
  }, [source, searchSessionId, throttledQuery, scopeParams, maxItems, startTime]);

  useEffect(() => {
    // Show no results if there is no query entered
    if (url == null) {
      setResults([]);
      setHasResponse(false);
      return undefined;
    }

    // Otherwise, make a request to /TypeAheadJson to fetch the results
    const controller = new AbortController();
    let resolved = false;

    fetch(url, {
      credentials: 'same-origin',
      signal: controller.signal,
    })
      .then(res => {
        if (res.ok) {
          return res.json();
        }

        throw new Error(res.statusText);
      })
      .then(res => {
        setResults(parseSearchResults(res, searchSessionId));
        setHasResponse(true);
      })
      .catch(error => {
        if (error.code !== error.ABORT_ERR) {
          console.error('useSearchResults: Error fetching typeahead results', error);
        }
      })
      .finally(() => {
        resolved = true;
      });

    // If the query changes before the fetch finishes, cancel the previous request and start a new
    // one.
    return () => {
      if (!resolved) {
        controller.abort();
      }
    };
  }, [url, searchSessionId]);

  const resultsWithImages = useSearchResultsWithImages(results);

  return {
    hasResponse,
    results: resultsWithImages,
    logImpression,
    logSelection,
    logSearchAction,
    searchSessionId,
  };
};

export default useSearchResults;
